import type { ReactNode } from 'react';
import { Info } from 'lucide-react';
import type { AccountMetricsResponse } from '@pnl/shared';
import { formatBrl } from '../../lib/format';
import { Tooltip } from '../Tooltip';
import { DailyResetCountdown } from './DailyResetCountdown';

interface Props {
  results: AccountMetricsResponse['results'];
}

function LimitCard({
  label,
  value,
  tone,
  tooltip,
}: {
  label: string;
  value: string;
  tone: 'pos' | 'neg' | 'neutral';
  tooltip?: ReactNode;
}) {
  const color =
    tone === 'pos' ? 'text-success-300' : tone === 'neg' ? 'text-danger-300' : 'text-ink-100';
  return (
    <div className="border-ink-800 bg-ink-900 rounded-lg border px-4 py-3 text-center">
      <p className="text-ink-400 flex items-center justify-center gap-1 text-xs font-medium">
        {label}
        {tooltip ? (
          <Tooltip label={tooltip}>
            <Info size={11} className="text-ink-500" />
          </Tooltip>
        ) : (
          <Info size={11} className="text-ink-500" />
        )}
      </p>
      <p className={'mt-1 text-lg font-bold tabular-nums ' + color}>{value}</p>
    </div>
  );
}

export function DailyLimits({ results }: Props) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <LimitCard
        label="Perda permitida hoje"
        value={formatBrl(results.todayAllowedLossCents)}
        tone="neutral"
        tooltip={
          <span>
            Este valor indica a quantidade de margem de perda permitida que tem num determinado dia,
            em relação à equidade atual da sua conta.
            <span className="text-ink-400 mt-2 block">
              A perda permitida de hoje será reposta em <DailyResetCountdown />.
            </span>
          </span>
        }
      />
      <LimitCard
        label="Perda máxima permitida"
        value={formatBrl(results.maxAllowedLossCents)}
        tone="neutral"
        tooltip="Este valor indica a quantidade de margem de perda permitida que tem no total, em relação à equidade atual da sua conta."
      />
      <LimitCard
        label="Lucros de hoje"
        value={formatBrl(results.todayPnlCents, { signed: true })}
        tone={results.todayPnlCents > 0 ? 'pos' : results.todayPnlCents < 0 ? 'neg' : 'neutral'}
        tooltip="Este valor mostra o seu P/L fechado e flutuante, calculado a partir da meia-noite (horário de Brasília)."
      />
    </div>
  );
}
